// src/services/apiService.js

import {
  validateGoogleToken,
  isGoogleAuthenticated,
} from "./googleAuthService";
import { API_ROUTES } from "../config/apiConstants";

// Dev uses proxy: VITE_API_BASE=/phapiv2 (goes to VITE_API_TARGET)
// Prod can set VITE_API_BASE to full URL if you prefer.
const API_BASE = import.meta.env.VITE_API_BASE || "/phapiv2";
const API_MANAGER_ROUTE = API_ROUTES.MANAGER_REGISTER;
const VENDOR_TOKEN = import.meta.env.VITE_APP_GROUPMANAGER_PUBLICTOKEN || ""; // static public token

/**
 * Core fetch wrapper
 * @param {string} endpoint e.g. "/managerRegisterV137/ManagerLogin"
 * @param {string} method
 * @param {object|null} body
 */
export const apiRequest = async (endpoint, method = "GET", body = null) => {
  const token = localStorage.getItem("jwtToken");

  const headers = {
    "Content-Type": "application/json",
    "X-Vendor": VENDOR_TOKEN,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${endpoint}`, opts);

  if (!res.ok) {
    let msg;
    try {
      const data = await res.json();
      msg = data.message || data.error || `HTTP ${res.status}`;
    } catch {
      const text = await res.text();
      msg =
        text && !text.includes("<!DOCTYPE") && !text.includes("<html")
          ? text
          : `Server error (${res.status}): ${res.statusText}`;
    }
    throw new Error(msg);
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  if (ct.includes("text/plain"))
    return { message: await res.text(), success: true };
  return { success: true };
};

/**
 * PHAPPS helper (direct URL). If you want proxy here too, add another proxy rule and switch to API_BASE.
 */
export const phappsRequest = async (
  endpoint,
  method = "GET",
  body = null,
  params = {}
) => {
  const token = localStorage.getItem("jwtToken");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const qs = new URLSearchParams(params).toString();
  const urlBase = import.meta.env.VITE_APP_DATA_API_URL;
  const url = `${urlBase}${endpoint}${qs ? `?${qs}` : ""}`;

  const opts = { method, headers };
  if (body) {
    opts.body = JSON.stringify(body);
  }

  // Debug logging
  if (import.meta.env.DEV) {
    console.log(`[phappsRequest] ${method} ${url}`, {
      body,
      params,
      headers,
      optsBody: opts.body,
    });
  }

  const res = await fetch(url, opts);

  if (!res.ok) {
    let msg;
    try {
      const data = await res.json();
      msg =
        data.comment ||
        data.message ||
        data.error ||
        `HTTP ${res.status}`;
    } catch {
      const text = await res.text();
      msg =
        text && !text.includes("<!DOCTYPE") && !text.includes("<html")
          ? text
          : `Server error (${res.status}): ${res.statusText}`;
    }
    const httpErr = new Error(msg);
    httpErr.status = res.status;
    throw httpErr;
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  if (ct.includes("text/plain"))
    return { message: await res.text(), success: true };
  return { success: true };
};

/** Login (stores token handled by caller if needed) */
export const login = async (username, password) => {
  return apiRequest(`/${API_MANAGER_ROUTE}/ManagerLogin`, "POST", {
    username,
    password,
  });
};

/** Optional: token validation flow with Google first */
export const validateToken = async () => {
  if (isGoogleAuthenticated()) return validateGoogleToken();

  const token = localStorage.getItem("jwtToken");
  if (!token) return false;

  try {
    const r = await apiRequest(
      `/${API_MANAGER_ROUTE}/ValidateManager`,
      "POST",
      null
    );
    // if it didn't throw, it's valid
    return true;
  } catch {
    return false;
  }
};

export const getData = async (endpoint) => apiRequest(endpoint, "GET");

export default { login, getData, apiRequest, phappsRequest, validateToken };
